import { useTranslation } from 'react-i18next'
import { Globe } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
import { supportedLanguages } from '@/lib/i18n'
import { API_BASE_URL } from '@/services/api'
import { cn } from '@/lib/utils'

interface LanguageSwitcherProps {
  className?: string
  variant?: 'default' | 'outline' | 'ghost'
  showLabel?: boolean
}

export function LanguageSwitcher({
  className,
  variant = 'ghost',
  showLabel = false
}: LanguageSwitcherProps) {
  const { i18n, t } = useTranslation()

  const changeLanguage = async (langCode: string) => {
    try {
      await i18n.changeLanguage(langCode)
      localStorage.setItem('i18nextLng', langCode)

      // Update the HTML lang attribute
      document.documentElement.lang = langCode

    // If user is logged in, save preference to backend
    const user = localStorage.getItem('user')
    if (user) {
      try {
        const response = await fetch(`${API_BASE_URL}/users/me`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
          body: JSON.stringify({ preferred_language: langCode }),
        })

        if (!response.ok) {
          console.error('[LanguageSwitcher] Failed to update language preference')
        }
      } catch (error) {
        console.error('[LanguageSwitcher] Error updating language preference:', error)
      }
    }
    } catch (error) {
      console.error('[LanguageSwitcher] Error changing language:', error)
    }
  }

  const currentLanguage = supportedLanguages[i18n.language as keyof typeof supportedLanguages]
    || supportedLanguages.en

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={variant}
          size="default"
          className={cn('gap-2', className)}
        >
          <Globe className="h-4 w-4" />
          <span className="text-xl">{currentLanguage.flag}</span>
          {showLabel && <span>{currentLanguage.name}</span>}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {Object.values(supportedLanguages).map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => changeLanguage(lang.code)}
            className={cn(
              'flex items-center gap-2 cursor-pointer',
              i18n.language === lang.code && 'bg-accent'
            )}
          >
            <span className="text-xl">{lang.flag}</span>
            <span>{lang.name}</span>
            {i18n.language === lang.code && (
              <span className="ml-auto text-xs">✓</span>
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
